import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Alert,
  Box,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import UpdateIcon from "@mui/icons-material/Update";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import { toast } from "react-toastify";
import { grnService } from "../../services/grn.service";
import {
  GoodsReceiptNote,
  ApproveGRNRequest,
  ApproveGRNResponse,
  GRNItem,
} from "../../types/grn.types";

interface EnhancedApprovalDialogProps {
  open: boolean;
  onClose: () => void;
  grn: GoodsReceiptNote | null;
  onSuccess: (response: ApproveGRNResponse) => void;
}

export const EnhancedApprovalDialog: React.FC<EnhancedApprovalDialogProps> = ({
  open,
  onClose,
  grn,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState("");
  const [approvalResult, setApprovalResult] =
    useState<ApproveGRNResponse | null>(null);
  const [step, setStep] = useState<"preview" | "approving" | "results">(
    "preview"
  );

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setStep("preview");
      setApprovalResult(null);
      setApprovalNotes("");
    }
  }, [open]);

  const handleApprove = async () => {
    if (!grn) return;

    setStep("approving");
    setLoading(true);

    try {
      const params: ApproveGRNRequest = {
        _id: grn._id,
        approval_notes: approvalNotes.trim() || undefined,
      };

      const response = await grnService.approveGRN(params);
      if (response.success) {
        setApprovalResult(response.data);
        setStep("results");
        toast.success("GRN approved successfully! Inventory has been updated.");
      }
    } catch (error: any) {
      console.error("Error approving GRN:", error);
      toast.error(error.message || "Failed to approve GRN");
      setStep("preview");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (approvalResult) {
      onSuccess(approvalResult);
    }
    onClose();
  };

  const formatCurrency = (amount: number, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount);
  };

  const getIngredientItemsPreview = () => {
    if (!grn) return [];
    return grn.items.filter((item) => item.create_as_ingredient);
  };

  const ingredientItems = getIngredientItemsPreview();

  if (!grn) return null;

  return (
    <Dialog
      open={open}
      onClose={step === "approving" ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: "90vh" } }}
    >
      <DialogTitle>
        <Box>
          <Typography variant="h6">
            {step === "preview" && "Approve GRN"}
            {step === "approving" && "Processing GRN Approval..."}
            {step === "results" && "GRN Approval Complete"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {grn.grn_number} • {grn.po_number}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {step === "approving" && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <LinearProgress sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Processing GRN Approval...
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Creating inventory entries and processing ingredient operations
            </Typography>
          </Box>
        )}

        {step === "preview" && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Approving this GRN will create inventory entries and process
              ingredient operations. The linked Purchase Order will be marked as
              "Ready for Invoice".
            </Alert>

            {/* GRN Summary */}
            <Paper sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
              <Typography variant="subtitle2" gutterBottom>
                GRN Summary
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Vendor
                  </Typography>
                  <Typography variant="body2">
                    {grn.po_reference.vendor_details.supplier_name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Total Value
                  </Typography>
                  <Typography variant="body2">
                    {formatCurrency(grn.total_received_value, grn.currency)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Items Received
                  </Typography>
                  <Typography variant="body2">
                    {
                      grn.items.filter((item) => item.received_quantity > 0)
                        .length
                    }{" "}
                    of {grn.items.length}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Ingredient Items
                  </Typography>
                  <Typography variant="body2" color="primary.main">
                    {ingredientItems.length} configured
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Ingredient Operations Preview */}
            {ingredientItems.length > 0 && (
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleIcon color="success" />
                    <Typography variant="subtitle2">
                      Ingredient Operations ({ingredientItems.length} items)
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {ingredientItems.map((item, index) => (
                      <ListItem
                        key={index}
                        divider={index < ingredientItems.length - 1}
                      >
                        <ListItemIcon>
                          {item.ingredient_exists ? (
                            <UpdateIcon color="warning" />
                          ) : (
                            <AddCircleIcon color="success" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography variant="body2" fontWeight={500}>
                                {item.item_name}
                              </Typography>
                              <Chip
                                size="small"
                                label={
                                  item.ingredient_exists
                                    ? "Update Existing"
                                    : "Create New"
                                }
                                color={
                                  item.ingredient_exists ? "warning" : "success"
                                }
                                variant="outlined"
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {item.received_quantity} {item.unit} •{" "}
                                {item.category || "Uncategorized"}
                                {item.brand_name && ` • ${item.brand_name}`}
                              </Typography>
                              {item.batch_number && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color="textSecondary"
                                >
                                  Batch: {item.batch_number}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            <TextField
              fullWidth
              label="Approval Notes (Optional)"
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              multiline
              rows={3}
              placeholder="Add any notes about the approval..."
              sx={{ mt: 2 }}
            />
          </Box>
        )}

        {step === "results" && approvalResult && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">
                {approvalResult.message}
              </Typography>
            </Alert>

            {/* Inventory Summary */}
            <Paper sx={{ p: 2, mb: 2, bgcolor: "success.50" }}>
              <Typography variant="subtitle2" gutterBottom color="success.main">
                Inventory Summary
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Items Added
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    {approvalResult.inventory_summary.items_added}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Value Added
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    {formatCurrency(
                      approvalResult.inventory_summary.total_value_added
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Ingredient Operations Results */}
            {approvalResult.ingredient_operations && (
              <>
                {/* Created Ingredients */}
                {approvalResult.ingredient_operations.ingredients_created
                  .length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <AddCircleIcon color="success" />
                        <Typography variant="subtitle2">
                          New Ingredients Created (
                          {
                            approvalResult.ingredient_operations
                              .ingredients_created.length
                          }
                          )
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {approvalResult.ingredient_operations.ingredients_created.map(
                          (ingredient, index) => (
                            <ListItem key={index}>
                              <ListItemIcon>
                                <CheckCircleIcon color="success" />
                              </ListItemIcon>
                              <ListItemText
                                primary={ingredient.name}
                                secondary={`SKU: ${ingredient.sku} • Initial stock: ${ingredient.initial_quantity}`}
                              />
                            </ListItem>
                          )
                        )}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Updated Ingredients */}
                {approvalResult.ingredient_operations.ingredients_updated
                  .length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <UpdateIcon color="warning" />
                        <Typography variant="subtitle2">
                          Ingredients Updated (
                          {
                            approvalResult.ingredient_operations
                              .ingredients_updated.length
                          }
                          )
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {approvalResult.ingredient_operations.ingredients_updated.map(
                          (ingredient, index) => (
                            <ListItem key={index}>
                              <ListItemIcon>
                                <UpdateIcon color="warning" />
                              </ListItemIcon>
                              <ListItemText
                                primary={ingredient.name}
                                secondary={`Added: ${ingredient.added_quantity} • New total: ${ingredient.new_total_stock}`}
                              />
                            </ListItem>
                          )
                        )}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Batches Created */}
                {approvalResult.ingredient_operations.batches_created.length >
                  0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CheckCircleIcon color="primary" />
                        <Typography variant="subtitle2">
                          Batches Created (
                          {
                            approvalResult.ingredient_operations.batches_created
                              .length
                          }
                          )
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {approvalResult.ingredient_operations.batches_created.map(
                          (batch, index) => (
                            <ListItem key={index}>
                              <ListItemIcon>
                                <CheckCircleIcon color="primary" />
                              </ListItemIcon>
                              <ListItemText
                                primary={batch.ingredient_name}
                                secondary={`Quantity: ${
                                  batch.quantity
                                } • Cost: ${formatCurrency(
                                  batch.cost_per_unit
                                )}/unit • From: ${batch.supplier_name}`}
                              />
                            </ListItem>
                          )
                        )}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Operations Skipped */}
                {approvalResult.ingredient_operations.operations_skipped
                  .length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <WarningIcon color="warning" />
                        <Typography variant="subtitle2">
                          Operations Skipped (
                          {
                            approvalResult.ingredient_operations
                              .operations_skipped.length
                          }
                          )
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {approvalResult.ingredient_operations.operations_skipped.map(
                          (operation, index) => (
                            <ListItem key={index}>
                              <ListItemIcon>
                                <WarningIcon color="warning" />
                              </ListItemIcon>
                              <ListItemText
                                primary={operation.item_name}
                                secondary={operation.reason}
                              />
                            </ListItem>
                          )
                        )}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Errors */}
                {approvalResult.ingredient_operations.errors.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <ErrorIcon color="error" />
                        <Typography variant="subtitle2">
                          Errors (
                          {approvalResult.ingredient_operations.errors.length})
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {approvalResult.ingredient_operations.errors.map(
                          (error, index) => (
                            <ListItem key={index}>
                              <ListItemIcon>
                                <ErrorIcon color="error" />
                              </ListItemIcon>
                              <ListItemText
                                primary={error.item_name}
                                secondary={error.error}
                              />
                            </ListItem>
                          )
                        )}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {step === "preview" && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <LoadingButton
              onClick={handleApprove}
              loading={loading}
              variant="contained"
              color="success"
            >
              Approve GRN
            </LoadingButton>
          </>
        )}

        {step === "results" && (
          <Button onClick={handleClose} variant="contained" color="primary">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
